import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { notesApi } from '../../lib/api.ts'
import { relativeTime } from '../../lib/relativeTime.ts'
import { useSession } from '../../hooks/useSession.ts'
import { useUsers } from '../../hooks/useTasks.ts'
import { EmptyState, ErrorState, Skeleton } from '../../ui/primitives.tsx'
import { SearchField } from '../../ui/SearchField.tsx'
import { Avatar, userColorVar } from '../shell/Avatar.tsx'
import { NoteEditor, type SyncState } from './NoteEditor.tsx'
import { NoteTasks } from './NoteTasks.tsx'
import styles from './notes.module.css'

/** Grows the title field to fit its wrapped text, so a long title is readable in full. */
function fitTitle(el: HTMLTextAreaElement | null): void {
  if (!el) return
  el.style.height = 'auto'
  // Everything here is `box-sizing: border-box`, so the height has to carry the border as well —
  // `scrollHeight` alone leaves the field two pixels short and the last line scrolls under it.
  const border = el.offsetHeight - el.clientHeight
  el.style.height = `${el.scrollHeight + border}px`
}

/**
 * Notes are an independent module, not an annex to tasks — the navigation says so with a separator
 * and this view has its own master–detail layout (brief, ui-spec §5.4).
 */
export function NotesView(): React.JSX.Element {
  const queryClient = useQueryClient()
  const session = useSession()
  const users = useUsers()

  const [activeId, setActiveId] = useState<number | null>(null)
  const [query, setQuery] = useState('')
  const [sync, setSync] = useState<SyncState>('syncing')
  const [typing, setTyping] = useState<{ name: string; colorVar: string }[]>([])
  const [savedAt, setSavedAt] = useState<string | null>(null)

  const notes = useQuery({
    queryKey: ['notes'],
    queryFn: async () => (await notesApi.list()).notes,
  })

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['notes'] })
  }

  const create = useMutation({
    mutationFn: (title: string) => notesApi.create(title),
    onSuccess: ({ note }) => {
      invalidate()
      setActiveId(note.id)
    },
  })
  const rename = useMutation({
    mutationFn: ({ id, title }: { id: number; title: string }) => notesApi.rename(id, title),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: (id: number) => notesApi.remove(id),
    onSuccess: () => {
      invalidate()
      setActiveId(null)
    },
  })

  const list = notes.data ?? []
  const filtered = query
    ? list.filter((n) => n.title.toLowerCase().includes(query.toLowerCase()))
    : list
  const active = list.find((n) => n.id === activeId) ?? null

  // `?note=3` opens that note straight away — what a mention notification links to.
  const [searchParams] = useSearchParams()
  const linkedNote = Number(searchParams.get('note'))
  useEffect(() => {
    if (Number.isInteger(linkedNote) && linkedNote > 0) setActiveId(linkedNote)
  }, [linkedNote])

  // Opening the module lands on the most recently touched note rather than an empty pane.
  useEffect(() => {
    if (activeId === null && list.length > 0) setActiveId(list[0]!.id)
  }, [activeId, list])

  const onSyncChange = useCallback((state: SyncState) => {
    setSync(state)
    if (state === 'saved') {
      setSavedAt(new Date().toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }))
    }
  }, [])

  const onTypingChange = useCallback((names: { name: string; colorVar: string }[]) => {
    setTyping(names)
  }, [])

  const titleRef = useRef<HTMLTextAreaElement | null>(null)

  /**
   * Sizing happens when the field attaches, not on a change of `activeId`.
   *
   * An effect keyed on the id measured nothing on `/notes?note=7` — the link a mention notification
   * follows. The id is set from the query string before the list has loaded, so at that moment
   * there is no note to render and no field to measure; by the time one existed, the id had not
   * changed and the effect never ran again. The title stayed one line tall with the rest of it
   * clipped.
   */
  const attachTitle = useCallback((el: HTMLTextAreaElement | null) => {
    titleRef.current = el
    fitTitle(el)
  }, [])

  // The wrap depends on the column width and on the font actually in use — the webfont lands after
  // first paint and rewraps the line, hence a second measure once it has.
  useEffect(() => {
    const fit = (): void => fitTitle(titleRef.current)
    void document.fonts.ready.then(fit)
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [])

  return (
    <div className={styles.wrap}>
      <aside className={styles.list}>
        <div className={styles.listBar}>
          <SearchField
            scope="view"
            value={query}
            onChange={setQuery}
            placeholder="Szukaj notatek"
            label="Szukaj notatek"
            className={styles.listSearch}
          />
          <button
            type="button"
            className={styles.newNote}
            title="Nowa notatka"
            aria-label="Nowa notatka"
            onClick={() => create.mutate('Nowa notatka')}
          >
            +
          </button>
        </div>

        <div className={styles.items}>
          {filtered.map((note) => {
            const author = users.data?.find((u) => u.id === note.updatedBy)
            return (
              <button
                key={note.id}
                type="button"
                className={`${styles.item}${note.id === activeId ? ` ${styles.itemActive}` : ''}`}
                onClick={() => setActiveId(note.id)}
              >
                <span className={styles.itemTitle}>{note.title}</span>
                <span className={styles.itemMeta}>
                  {author && (
                    <Avatar
                      name={author.displayName}
                      colorVar={userColorVar(author.id)}
                      size={14}
                    />
                  )}
                  <span className={styles.itemTime}>{relativeTime(note.updatedAt)}</span>
                  {note.id === activeId && typing.length > 0 && (
                    <span
                      className={styles.typingDot}
                      style={{ ['--user' as string]: `var(${typing[0]!.colorVar})` }}
                      title={`${typing[0]!.name} teraz pisze`}
                    />
                  )}
                </span>
              </button>
            )
          })}

          {notes.isPending && (
            <div className={styles.listLoading}>
              <Skeleton height={34} delay={0} />
              <Skeleton height={34} delay={200} />
              <Skeleton height={34} delay={400} />
            </div>
          )}

          {notes.isError && (
            <div className={styles.listEmpty}>
              <ErrorState
                text="Nie udało się wczytać listy notatek."
                onRetry={() => void notes.refetch()}
              />
            </div>
          )}

          {/* Two different empties. The search one used to be missing entirely, because this
              tested `list`, not `filtered` — typing a phrase with no matches emptied the column
              and said nothing at all. */}
          {!notes.isPending && !notes.isError && filtered.length === 0 && (
            <div className={styles.listEmpty}>
              {list.length === 0 ? (
                <EmptyState
                  text="Nie ma jeszcze notatek"
                  variant="plain"
                  action={{ label: 'Nowa notatka', onClick: () => create.mutate('Nowa notatka') }}
                />
              ) : (
                <EmptyState
                  text={`Żadna notatka nie pasuje do „${query}"`}
                  variant="plain"
                  action={{ label: 'Wyczyść', onClick: () => setQuery('') }}
                />
              )}
            </div>
          )}
        </div>
      </aside>

      <section className={styles.detail}>
        {!active || !session.data ? (
          <div className={styles.empty}>Wybierz notatkę albo utwórz nową.</div>
        ) : (
          <div className={styles.doc}>
            <div className={styles.titleRow}>
              <textarea
                key={active.id}
                ref={attachTitle}
                className={styles.title}
                defaultValue={active.title}
                rows={1}
                aria-label="Tytuł notatki"
                onInput={(e) => fitTitle(e.currentTarget)}
                onBlur={(e) => {
                  // A multi-line field can now receive a pasted line break; a title has none.
                  const title = e.target.value.replace(/\s+/g, ' ').trim()
                  if (title) e.target.value = title
                  fitTitle(e.target)
                  if (title && title !== active.title) rename.mutate({ id: active.id, title })
                }}
                onKeyDown={(e) => {
                  // A title is one line of meaning even when it wraps: Enter commits, never breaks.
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.currentTarget.blur()
                  }
                }}
              />
              <span className={styles.sync}>
                {sync === 'syncing' ? (
                  <>
                    <span className={styles.syncRing} aria-hidden="true" />
                    synchronizuję
                  </>
                ) : (
                  `zapisano ${savedAt ?? ''}`
                )}
              </span>
            </div>

            <div className={styles.metaLine}>
              <span className={styles.noteRef}>note:{active.id}</span>
              {typing.map((who) => (
                <span key={who.name} className={styles.typingWho}>
                  <span
                    className={styles.typingDot}
                    style={{ ['--user' as string]: `var(${who.colorVar})`, marginLeft: 0 }}
                  />
                  <span style={{ color: `var(${who.colorVar})` }}>{who.name} edytuje teraz</span>
                </span>
              ))}
              <button
                type="button"
                className={styles.deleteNote}
                onClick={() => {
                  if (window.confirm(`Usunąć notatkę „${active.title}”?`)) remove.mutate(active.id)
                }}
              >
                usuń notatkę
              </button>
            </div>

            {/* Nad treścią, bo „czego to dotyczy" czyta się przed samą treścią, a nie po niej —
                a notatki bywają na kilka ekranów (ADR 0012). */}
            <NoteTasks noteId={active.id} />

            <NoteEditor
              key={active.id}
              noteId={active.id}
              me={session.data}
              onSyncChange={onSyncChange}
              onTypingChange={onTypingChange}
            />
          </div>
        )}
      </section>
    </div>
  )
}
